/* eslint-disable prettier/prettier */
import React, {
  useEffect,
  useRef,
  useCallback,
  useState,
  useMemo,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';

import { format, parseISO, addDays } from 'date-fns';

import { FaTrashAlt } from 'react-icons/fa';
import { Container, Button, Row, Col } from 'react-bootstrap';

import api from '../../services/api';

import { useToast } from '../../hooks/toast';

import Input from '../../components/Input';
import SelectSync from '../../components/SelectSync';
import Loading from '../../components/Loading';
import Modal from '../../components/Modal';
import Agendamento from '../../components/Agendamento';
import Paginacao from '../../components/Paginacao';

import getValidationErrors from '../../utils/getValidationErrors';

import { PacienteLinha, PacienteLista } from './styles';

import { useAgenda } from '../../hooks/agenda';

import getResponseErrors from '../../utils/getResponseErrors';

import {
  optionsStatusAgendamento,
  formataTelefone,
  STATUS_AGENDA,
} from '../../utils/funcoes';

interface IAgendamento {
  AgeCodigo: number;
  AgeConfCodigo: number;
  AgeInicio: Date;
  AgeFim: Date;
  AgeDataFormatada: string;
  AgeDataFormatadaApi: string;
  AgeHoraFormatada: string;
  AgeStatus: number;
  AgeConfirmado: number;
  AgeStatusFormatado: string;
  PacNome: string;
  PacTelefone: string;
  PacCelular: string;
  PacTelefonesFormatado: string;
  ConNome: string;
  CliNomeAbreviado: string;
}

export interface IAgendamentoSelecionado {
  AgeCodigo: number;
  AgeConfCodigo: number;
  NomeAgenda?: string;
}

interface IFormBusca {
  PacNome: string;
  AgeConfCodigo: number;
  AgeStatus: number;
  AgeInicio: Date;
  AgeFim: Date;
}

interface IPaginacao {
  PacNome?: string;
  AgeConfCodigo?: number;
  AgeStatus?: number;
  AgeInicio: Date;
  AgeFim: Date;
  dataFake: Date;
  limite: number;
  pagina: number;
}

export interface IOption {
  value: string;
  label: string;
  isDisabled?: boolean;
}

function cleanString(input: string): string {
  let output = '';
  for (let i = 0; i < input.length; i += 1) {
    if (
      input.charCodeAt(i) <= 127 ||
      (input.charCodeAt(i) >= 160 && input.charCodeAt(i) <= 255)
    ) {
      output += input.charAt(i);
    }
  }
  return output;
}

const AgendaBusca: React.FC = () => {
  const { addToast } = useToast();
  const history = useHistory();
  const formRefBusca = useRef<FormHandles>(null);
  const [tipoFalta, setTipoFalta] = useState(false);
  const [showAgendamento, setShowAgendamento] = useState(false);
  const [agendamentoSelecionado, setAgendamentoSelecionado] = useState<
    IAgendamentoSelecionado | undefined
  >(undefined);
  const [agendamentos, setAgendamentos] = useState<IAgendamento[] | undefined>(
    undefined,
  );
  const [loading, setLoading] = useState(false);
  const [paginacao, setPaginacao] = useState<IPaginacao>();
  const [totalRegistros, setTotalRegistros] = useState<number | undefined>(
    undefined,
  );
  const { retornaDadosAgenda } = useAgenda();
  const [optionsAgenda, setOptionsAgenda] = useState<IOption[]>([]);

  useEffect(() => {
    async function loadDados(): Promise<void> {
      const options = await retornaDadosAgenda();
      setOptionsAgenda(options);
    }
    loadDados();
  }, [retornaDadosAgenda]);

  function useQuery(): URLSearchParams {
    return new URLSearchParams(useLocation().search);
  }

  const query = useQuery();

  const initialData = useMemo(() => {
    let AgeInicio = format(new Date(), 'yyyy-MM-dd');
    let AgeFim = format(addDays(new Date(), 5), 'yyyy-MM-dd');
    if (query.get('data') && query.get('data')?.length === 10) {
      AgeInicio = format(
        parseISO(`${query.get('data')} 03:00:00`),
        'yyyy-MM-dd',
      );
      if (query.get('datafim') && query.get('datafim')?.length === 10) {
        AgeFim = format(
          parseISO(`${query.get('datafim')} 03:00:00`),
          'yyyy-MM-dd',
        );
      } else {
        AgeFim = format(
          parseISO(`${query.get('data')} 03:00:00`),
          'yyyy-MM-dd',
        );
      }
    }

    const periodo = {
      AgeInicio,
      AgeFim,
    };

    const AgeStatus = query.get('status') || 0;
    const AgeConfCodigo = query.get('agenda') || 0;
    const PacNome = query.get('paciente') || '';

    return { ...periodo, AgeStatus, AgeConfCodigo, PacNome };
  }, [query]);

  const handleSubmitBusca = useCallback(async (dados: IFormBusca) => {
    try {
      formRefBusca.current?.setErrors({});
      const schema = Yup.object().shape({
        PacNome: Yup.string(),
        AgeConfCodigo: Yup.number()
          .nullable()
          .transform(x => x || null),
        AgeStatus: Yup.number()
          .nullable()
          .transform(x => x || null),
        AgeInicio: Yup.date().required('Início precisa ser uma data'),
        AgeFim: Yup.date().required('Fim precisa ser uma data'),
      });

      await schema.validate(dados, {
        abortEarly: false,
      });

      setAgendamentos(undefined);

      const { AgeInicio, AgeFim, PacNome, AgeConfCodigo, AgeStatus } = dados;

      setPaginacao({
        ...(PacNome.toString().length > 1 ? { PacNome } : {}),
        ...(AgeConfCodigo.toString().length > 0
          ? { AgeConfCodigo: Number(AgeConfCodigo) }
          : {}),
        ...(AgeStatus.toString().length > 0
          ? { AgeStatus: Number(AgeStatus) }
          : {}),
        AgeInicio: parseISO(AgeInicio.toString()),
        AgeFim: parseISO(AgeFim.toString()),
        dataFake: new Date(),
        pagina: 1,
        limite: 20,
      });
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        const errors = getValidationErrors(error);
        formRefBusca.current?.setErrors(errors);
      }
    }
  }, []);

  const handleDeleteFalta = useCallback(
    async (codigo: number) => {
      try {
        setLoading(true);

        await api.delete(`/agendamentos/falta/${codigo}`);
        setPaginacao(state => {
          return state ? { ...state, dataFake: new Date() } : undefined;
        });
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível buscar os dados',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    },
    [addToast],
  );

  useEffect(() => {
    formRefBusca.current?.submitForm();
  }, []);

  useEffect(() => {
    async function loadDados(): Promise<void> {
      setLoading(true);
      try {
        if (!paginacao) return;
        setTipoFalta(false);

        let parametros = '';
        const {
          AgeInicio,
          AgeFim,
          PacNome,
          AgeConfCodigo,
          AgeStatus,
        } = paginacao;

        if (PacNome)
          parametros += `&PacNome=${cleanString(
            PacNome.normalize('NFD')
              .replace(/[\u0300-\u036f]/g, '')
              .replace("'", ' '),
          )}`;

        if (AgeConfCodigo) parametros += `&AgeConfCodigo=${AgeConfCodigo}`;
        if (AgeStatus !== undefined) {
          parametros += `&AgeStatus=${AgeStatus}`;
          if (AgeStatus === STATUS_AGENDA.FALTA) setTipoFalta(true);
        }

        const InicioFormatado = format(AgeInicio, 'yyyy-MM-dd');
        const FimFormatado = format(AgeFim, 'yyyy-MM-dd');

        const response = await api.get(
          `/agendamentos/busca?&pagina=${paginacao.pagina}&limite=${paginacao.limite}&AgeInicio=${InicioFormatado}&AgeFim=${FimFormatado}${parametros}`,
        );
        const responseApi: IAgendamento[] = response.data[0];

        setTotalRegistros(
          response.data[1] === 0 ? undefined : response.data[1],
        );

        const agendamentosFormatados = responseApi.map(age => {
          const inicio = parseISO(age.AgeInicio.toString());

          const PacTelefonesFormatado =
            age.PacTelefone && age.PacTelefone.length > 0
              ? `${formataTelefone(age.PacCelular)} - ${formataTelefone(
                age.PacTelefone,
              )}`
              : formataTelefone(age.PacCelular);

          return {
            ...age,
            AgeDataFormatada: format(inicio, 'dd/MM/yyyy'),
            AgeDataFormatadaApi: format(inicio, 'yyyy-MM-dd'),
            AgeHoraFormatada: `${format(inicio, 'HH:mm')} - ${format(
              parseISO(age.AgeFim.toString()),
              'HH:mm',
            )}`,
            AgeStatusFormatado:
              optionsStatusAgendamento.find(sta =>
                age.AgeStatus === STATUS_AGENDA.AGENDADO
                  ? sta.value ===
                  (age.AgeConfirmado + 1 >= 2 ? 2 : 1).toString()
                  : sta.value === age.AgeStatus.toString(),
              )?.label || 'vazio',
            PacTelefonesFormatado,
          };
        });

        setAgendamentos(agendamentosFormatados);
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível buscar agendamentos',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    }

    if (paginacao) loadDados();
  }, [addToast, paginacao]);

  const handleProximaPagina = useCallback((page: number) => {
    setPaginacao(state => {
      return state && { ...state, pagina: page };
    });
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const handleOnChangeVazio = useCallback(() => { }, []);

  return (
    <Container fluid className="mt-2">
      {loading && <Loading />}

      {showAgendamento && agendamentoSelecionado && (
        <Modal
          show={showAgendamento}
          titulo={`Agendamento: ${agendamentoSelecionado.AgeCodigo} - ${agendamentoSelecionado.NomeAgenda}`}
          handleFecharModal={
            () => {
              setAgendamentoSelecionado(undefined);
              setShowAgendamento(false);
            }
            // eslint-disable-next-line react/jsx-curly-newline
          }
        >
          <Agendamento
            agendaId={agendamentoSelecionado.AgeConfCodigo}
            perfilId={undefined}
            data={undefined}
            AgeCodigo={agendamentoSelecionado.AgeCodigo}
            handleFecharAgendamento={agenda => {
              setShowAgendamento(false);
              setPaginacao(state => {
                return state ? { ...state, dataFake: new Date() } : undefined;
              });
            }}
          />
        </Modal>
      )}
      <div className="mb-2 p-2 bg-white border rounded">
        <div className="d-flex justify-content-between align-items-center mb-1">
          <h6 id="topoBusca">Agenda pesquisa</h6>
          <Button
            type="button"
            variant="outline-primary"
            size="sm"
            onClick={() => history.push('/agendas')}
          >
            Voltar para agendas
          </Button>
        </div>

        <Form
          ref={formRefBusca}
          initialData={initialData}
          onSubmit={handleSubmitBusca}
        >
          <Row noGutters>
            <Col sm={12} md={5} lg={3} className="pr-1">
              <Input
                name="PacNome"
                label="Nome"
                maxLength={100}
                minLength={3}
              />
            </Col>
            <Col sm={6} md={3} lg={2} className="pr-1">
              <Input name="AgeInicio" label="Período" type="date" />
            </Col>
            <Col sm={6} md={4} lg={2} className="pr-1">
              <Input name="AgeFim" label="Até" type="date" />
            </Col>
            <Col sm={12} md={4} lg={2} className="pr-1">
              <SelectSync
                name="AgeConfCodigo"
                label="Agenda"
                handleSelectChange={handleOnChangeVazio}
                options={optionsAgenda}
              />
            </Col>
            <Col sm={6} md={4} lg={2} className="pr-1">
              <SelectSync
                name="AgeStatus"
                label="Status"
                handleSelectChange={handleOnChangeVazio}
                options={optionsStatusAgendamento}
              />
            </Col>
            <Col sm={12} md={3} lg={1} className="pr-1">
              <Button
                type="submit"
                size="sm"
                className="mt-3"
                variant="primary"
              >
                BUSCAR
              </Button>
            </Col>
          </Row>
        </Form>
      </div>

      <div className="bg-white border p-2 rounded">
        {agendamentos ? (
          <>
            <b>Total de {totalRegistros} registros</b>
            <Row noGutters className="bg-light py-2 border-top border-bottom">
              <Col md={2} lg={1}>
                <b>Data</b>
              </Col>
              <Col md={2} lg={1}>
                <b>Horário</b>
              </Col>
              <Col md={3} lg={3}>
                <b>Agenda</b>
              </Col>
              <Col md={3} lg={2}>
                <b>Paciente</b>
              </Col>
              <Col md={2} lg={2}>
                <b>Telefone</b>
              </Col>
              <Col md={2} lg={1}>
                <b>Status</b>
              </Col>
              <Col md={2} lg={1}>
                -
              </Col>
              <Col md={2} lg={1}>
                -
              </Col>
            </Row>
            {agendamentos.map(age => (
              <PacienteLinha
                className="row no-gutters"
                key={`age-${age.AgeCodigo}`}
                data-id={age.AgeCodigo}
              >
                <div className="col-md-2 col-lg-1">
                  <PacienteLista>{age.AgeDataFormatada}</PacienteLista>
                </div>
                <div className="col-md-2 col-lg-1">
                  <PacienteLista>{age.AgeHoraFormatada}</PacienteLista>
                </div>
                <div className="col-md-3 col-lg-3">
                  <PacienteLista>
                    {age.ConNome} - {age.CliNomeAbreviado}
                  </PacienteLista>
                </div>
                <div className="col-md-3 col-lg-2">
                  <PacienteLista>{age.PacNome}</PacienteLista>
                </div>
                <div className="col-md-2 col-lg-2">
                  <PacienteLista>{age.PacTelefonesFormatado}</PacienteLista>
                </div>
                <div className="col-md-2 col-lg-1">
                  <PacienteLista>{age.AgeStatusFormatado}</PacienteLista>
                </div>
                <div className="col-md-2 col-lg-1">
                  <PacienteLista>
                    <Button
                      type="button"
                      variant="outline-success"
                      size="sm"
                      data-id={age.AgeCodigo}
                      onClick={() => {
                        setAgendamentoSelecionado({
                          AgeCodigo: age.AgeCodigo,
                          AgeConfCodigo: age.AgeConfCodigo,
                          NomeAgenda: age.ConNome,
                        });
                        setShowAgendamento(true);
                      }}
                    >
                      {!tipoFalta ? 'Ver Agendamento' : 'Reagendar'}
                    </Button>
                  </PacienteLista>
                </div>
                <div className="col-md-2 col-lg-1">
                  <PacienteLista>
                    {!tipoFalta ? (
                      <Button
                        type="button"
                        variant="outline-secondary"
                        size="sm"
                        data-id={age.AgeCodigo}
                        onClick={
                          () =>
                            history.push(
                              `agenda/${age.AgeConfCodigo}?data=${age.AgeDataFormatadaApi}`,
                              // eslint-disable-next-line prettier/prettier
                            )
                          // eslint-disable-next-line prettier/prettier
                        }
                      >
                        Ver na Agenda
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        variant="outline-danger"
                        size="sm"
                        data-id={age.AgeCodigo}
                        onClick={() => handleDeleteFalta(age.AgeCodigo)}
                      >
                        <FaTrashAlt size={18} />
                      </Button>
                    )}
                  </PacienteLista>
                </div>
              </PacienteLinha>
            ))}
          </>
        ) : (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: '250px' }}
          >
            <h5>
              Pesquise pelo nome do paciente, data do agendamento, status ou
              agenda.
            </h5>
          </div>
        )}

        <Paginacao
          limitePagina={paginacao?.limite || 20}
          paginasVizinhas={2}
          totalRegistros={totalRegistros}
          paginaInicio={paginacao?.pagina}
          handleProximaPagina={handleProximaPagina}
        />
      </div>
    </Container>
  );
};

export default AgendaBusca;
